import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import * as Y from 'yjs';
import type { Awareness } from 'y-protocols/awareness';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  ConnectionMode,
  Panel,
  useReactFlow,
  useStore as useRFStore,
  applyEdgeChanges,
  applyNodeChanges,
  type Connection,
  type Edge as RFEdge,
  type EdgeChange,
  type Node as RFNode,
  type NodeChange,
  type ReactFlowProps,
  type OnSelectionChangeParams,
  type ReactFlowState,
} from '@xyflow/react';
import { MergeMap } from "@/features/canvas/engine/primitives/persistence/merge-map";
import '@xyflow/react/dist/style.css';
import styles from './canvas-engine.module.css';
import { CanvasZoomControls } from "@/features/canvas/engine/surfaces/canvas-zoom-controls";
import { Imperative } from '@/features/canvas/engine/wiring/imperative';
import { useNodesFromYjs } from '@/features/canvas/engine/hooks/use-nodes-from-yjs';
import { useEdgesFromYjs, collectDotMarkerColors, safeColorId } from '@/features/canvas/engine/hooks/use-edges-from-yjs';
import { useCanvasUndoManager } from '@/features/canvas/engine/hooks/use-canvas-undo-manager';
import { nodeTypeMap } from '@/features/canvas/engine/nodes';
import { NulaEdge } from '@/features/canvas/engine/edges/nula-edge';
import { createEdgeFromConnection } from '@/features/canvas/engine/edges/create-edge';
import { deleteEdges } from '@/features/canvas/engine/edges/delete-edges';
import { reconnectEdgeYjs } from '@/features/canvas/engine/edges/reconnect-edge-yjs';
import { setEdgeLocked } from '@/features/canvas/engine/edges/set-edge-locked';
import { bringToFront, sendToBack, bringForward, sendBackward } from "@/features/canvas/engine/primitives/persistence/z-order";
import { EdgeActionsContext, type EdgeActions } from "@/features/canvas/engine/wiring/contexts/edge-actions-context";
import { NulaConnectionLine } from '@/features/canvas/engine/edges/nula-connection-line';
import { InFlightReconnectContext, type InFlightReconnectRef } from '@/features/canvas/engine/edges/in-flight-reconnect-context';
import { InFlightEdgeBroadcaster } from '@/features/canvas/engine/edges/in-flight-edge-broadcaster';
import { FitViewOnLoad } from "@/features/canvas/engine/surfaces/fit-view-on-load";

const edgeTypeMap = {
  default: NulaEdge,
  visual: NulaEdge,
  logical: NulaEdge,
};
import {
  FrameDropTargetContext,
  useFrameDropTarget,
} from '@/features/canvas/engine/hooks/use-frame-drop-target';
import { findFrameUnderPoint } from "@/features/canvas/engine/primitives/geometry/geometry";
import { useAutoAttach } from '@/features/canvas/engine/hooks/use-auto-attach';
import { useFrameDelete } from '@/features/canvas/engine/hooks/use-frame-delete';
import { useYjsNodeSync } from '@/features/canvas/engine/hooks/use-yjs-node-sync';
import { EmptyCanvas } from "@/features/canvas/engine/surfaces/empty-canvas";
import { useCanvasKeyboard } from '@/features/canvas/engine/a11y/use-canvas-keyboard';
import { ParentLabelProvider } from '@/features/canvas/engine/a11y/parent-label-context';
import { CanvasDocContext } from "@/features/canvas/engine/wiring/contexts/canvas-doc-context";
import { CanvasBindingContext } from "@/features/canvas/engine/wiring/contexts/canvas-binding-context";
import { canvasRoots, type CanvasBinding, TOP_LEVEL_BINDING } from "@/features/canvas/engine/wiring/canvas-roots";
import { ConnectorModeContext } from "@/features/canvas/engine/wiring/contexts/connector-mode-context";
import { CanvasReadOnlyContext } from "@/features/canvas/engine/wiring/contexts/canvas-read-only-context";
import { CanvasKeyboardShortcuts } from "@/features/canvas/engine/wiring/shortcuts/canvas-keyboard-shortcuts";
import { projectSpatialNodes } from "@/features/canvas/engine/primitives/geometry/spatial-projection";
import { useFrameDragCache } from '@/features/canvas/engine/hooks/use-frame-drag-cache';
import { CanvasAwarenessLayer } from '@/features/canvas/engine/render/awareness';
import type { CanvasAwarenessState } from '@/features/canvas/engine/render/awareness-types';
import { DrawingOverlay } from '@/features/canvas/engine/tools/drawing-overlay';
import { EraserOverlay } from '@/features/canvas/engine/tools/eraser-overlay';
import { HelperLinesRenderer } from "@/features/canvas/engine/surfaces/helper-lines-renderer";
import { useHelperLines } from '@/features/canvas/engine/hooks/use-helper-lines';
import { useScreenToCanvasCoords } from '@/features/canvas/engine/hooks/use-screen-to-canvas-coords';
import { useHorizontalScrollPan } from '@/features/canvas/engine/hooks/use-horizontal-scroll-pan';
import { MultiSelectionToolbar } from '@/features/canvas/engine/selection/surfaces/floating/multi-selection-toolbar';
import { CanvasContextMenu, useCanvasContextMenu } from '@/features/canvas/engine/selection/surfaces/menu/canvas-context-menu';
import type { PointerModeProps } from '@/features/canvas/engine/hooks/use-pointer-mode';
import { applyPendingSelect } from "@/features/canvas/engine/primitives/persistence/apply-pending-select";

export interface CanvasEngineProps {
  doc: Y.Doc;
  /**
   * Where the canvas's Yjs roots live in `doc`. Defaults to top-level
   * (standalone canvas — byte-identical to legacy inline access). A nested
   * binding embeds the canvas under a parent map (e.g. a document surface diagram).
   * Provided to descendants via CanvasBindingContext.
   */
  binding?: CanvasBinding;
  /**
   * Awareness for the canvas room. Accepted as `Awareness | null` because the
   * engine never dereferences it directly — remote presence flows in via the
   * `remoteStates` prop + the `report*` callbacks, not this prop. Callers that
   * legitimately hold `Awareness | null` therefore need no cast.
   */
  awareness: Awareness | null;
  /** The Yjs clientID used to tag this client's writes (for per-user undo). */
  clientId: number;
  /** Called when the user clicks the empty pane. Position is in canvas (flow) coordinates.
   *  frameId is the id of the frame under the click point, or null if the click landed on
   *  the bare canvas — lets the shell attach the new node to the frame at the right position. */
  onPaneClick?: (position: { x: number; y: number }, frameId: string | null) => void;
  /**
   * Map of remote peer awareness states keyed by Yjs clientID. Produced by the
   * shell hook `useCanvasAwareness` and threaded through as a prop to keep the
   * engine layer free of session/user concerns. Optional — when absent or
   * empty, no remote cursors / outlines render.
   */
  remoteStates?: Map<number, CanvasAwarenessState>;
  /**
   * Called from the engine when the local pointer moves over the canvas.
   * Coordinates are in canvas (unzoomed, unpanned) space — the engine handles
   * the screen↔canvas transform here so the shell hook
   * (`useCursorAwareness`) just persists the resolved value via setLocalState.
   * Optional — when absent, pointer awareness is disabled.
   */
  reportPointer?: (point: { x: number; y: number } | null) => void;
  /**
   * Called whenever the pointer moves over the canvas, with the current
   * position in canvas (flow) coordinates. Used by the shell to track the
   * cursor so paste operations place nodes under the mouse rather than at the
   * copy origin.
   */
  onCursorMove?: (point: { x: number; y: number }) => void;
  /**
   * Called from the engine when the local selection changes. The argument is
   * the array of currently-selected node ids. Optional — when absent,
   * selection awareness is disabled.
   */
  reportSelection?: (ids: string[]) => void;
  reportEdgeSelection?: (ids: string[]) => void;
  /**
   * Called from the engine while the user is dragging an edge endpoint
   * (fresh-create or reconnect). The argument is a SharedInFlightEdge with
   * the fixed-end handle reference + live cursor position; pass null on
   * drag-end. Optional — when absent, in-flight edge awareness is disabled.
   */
  reportInFlightEdge?: (edge: import('@/features/canvas/engine/render/awareness-types').SharedInFlightEdge | null) => void;
  /**
   * Initial viewport applied on mount. The shell resolves this by awaiting
   * `getViewport(canvasId, userId)` from the IDB viewport store before
   * mounting the engine. When omitted, ReactFlow renders at the default
   * (0, 0, zoom 1).
   */
  defaultViewport?: { x: number; y: number; zoom: number };
  /**
   * Called by ReactFlow on every viewport change (pan/zoom). The shell wires
   * this to the debounced save in `useViewportPersistence` so frame-rate
   * pan events coalesce into a single IDB write per quiet window.
   */
  onViewportChange?: (v: { x: number; y: number; zoom: number }) => void;
  /** When true, renders the DrawingOverlay for freehand input. */
  isDrawing?: boolean;
  /** Stroke color forwarded to DrawingOverlay. */
  drawingColor?: string;
  /** Stroke width forwarded to DrawingOverlay. */
  drawingWidth?: number;
  /** Stroke opacity forwarded to DrawingOverlay (0–1). Used for highlighter mode. */
  drawingOpacity?: number;
  /** When true, renders the EraserOverlay for erasing freedraw nodes. */
  isErasing?: boolean;
  /** When true, every node body becomes a connector source — drag from
   *  anywhere on a node to draw an edge to another node. Activated by the
   *  Connector tool (shortcut `L`). */
  connectorMode?: boolean;
  /** Pan/selection behaviour resolved from the active tool via usePointerMode. */
  pointerMode?: PointerModeProps;
  /** Node id to auto-select on next yjsNodes update (e.g. after expand-create). */
  pendingSelectId?: string | null;
  /** When true, disables all mutation interactions — no drag, delete, or keyboard edits. */
  readOnly?: boolean;
  /** When provided, called per node to resolve an additional CSS class (e.g. diff highlights). */
  nodeClassName?: (node: { id: string }) => string;
  /**
   * When true, calls `fitView` once after nodes are measured. Used by the
   * diff/snapshot panes so both sides land on framed content regardless of
   * where the node graph sits in flow-space. No-op when there are no nodes.
   */
  fitOnMount?: boolean;
  /**
   * When provided, the zoom-controls pill renders a Frames toggle button
   * (in the slot the minimap toggle used to occupy). The shell handles
   * the open/close state — the engine just dispatches the toggle. Pair
   * with `framesPanelOpen` so the button reflects current state. When
   * omitted, the button doesn't render — keeps standalone mounts
   * (CanvasViewer, snapshot diffs) unchanged.
   */
  onOpenFramesPanel?: () => void;
  framesPanelOpen?: boolean;
  /** When true, suppresses the zoom-controls panel. Used by preview/thumbnail mounts. */
  hideControls?: boolean;
  /**
   * Optional content rendered INSIDE the React Flow context (so it can use
   * `useReactFlow`/the RF store). The engine stays ignorant of what it is — the
   * shell injects context-dependent helpers here (e.g. the card-preview
   * snapshotter) without the engine taking on persistence concerns.
   */
  inFlowSlot?: ReactNode;
}

/**
 * Pure engine seam. Renders a React Flow canvas backed by `doc.getMap('nodes')`
 * and `doc.getMap('edges')`. Has no knowledge of routes, auth, persistence,
 * or shells — wire those at the call site (M9). See client.md for the seam
 * contract.
 *
 * React Flow node container exception: uses <div> wrappers per React Flow v12
 * requirements. See engine/README §Raw-HTML exception for context.
 */
export function CanvasEngine(props: CanvasEngineProps) {
  // ReactFlowProvider must wrap any consumer of useReactFlow (the inner
  // body uses it via getInternalNode for absolute-position lookups during
  // drag). Splitting into a body component keeps the wrapper opt-in; tests
  // that mount <CanvasEngine /> directly still get the provider.
  return (
    <ReactFlowProvider>
      <CanvasEngineBody {...props} />
    </ReactFlowProvider>
  );
}

function CanvasEngineBody({
  doc,
  binding,
  awareness: _awareness,
  clientId,
  onPaneClick,
  remoteStates,
  reportPointer,
  reportSelection,
  reportEdgeSelection,
  reportInFlightEdge,
  onCursorMove,
  defaultViewport,
  onViewportChange,
  isDrawing,
  drawingColor,
  drawingWidth,
  drawingOpacity,
  isErasing,
  connectorMode,
  pointerMode,
  pendingSelectId,
  readOnly = false,
  nodeClassName,
  fitOnMount,
  onOpenFramesPanel,
  framesPanelOpen,
  hideControls = false,
  inFlowSlot,
}: CanvasEngineProps) {
  // Resolve the canvas roots once for this engine. The binding prop defaults to
  // top-level (standalone canvas — byte-identical to legacy inline access). The
  // child hooks below read the same binding via CanvasBindingContext (provided
  // at the JSX root), so they resolve the identical roots.
  const resolvedBinding = binding ?? TOP_LEVEL_BINDING;
  const roots = useMemo(() => canvasRoots(doc, resolvedBinding), [doc, resolvedBinding]);
  const yjsNodes = useNodesFromYjs(doc);
  const yjsEdges = useEdgesFromYjs(doc);
  const undoApi = useCanvasUndoManager(doc, clientId);

  // Local edges array — merges Yjs snapshot with RF-managed local fields
  // (currently just `selected`). Without this, applyEdgeChanges has nowhere
  // to write the `selected: true` flag when the user clicks an edge, and
  // selection silently fails. The Yjs snapshot is authoritative for
  // source/target/data; selection is purely local.
  const [localEdges, setLocalEdges] = useState<RFEdge[]>([]);
  useEffect(() => {
    setLocalEdges((prev) => {
      const prevById = new Map(prev.map((e) => [e.id, e] as const));
      return yjsEdges.map((e) => {
        const prevEdge = prevById.get(e.id);
        // Preserve `selected` from prev local state; everything else comes
        // from Yjs.
        return prevEdge ? { ...e, selected: prevEdge.selected } as RFEdge : (e as RFEdge);
      });
    });
  }, [yjsEdges]);

  const onEdgesChange = useCallback((changes: EdgeChange[]) => {
    // Selection / remove changes update local state. Yjs is settled-state
    // storage; the create path writes through createEdgeFromConnection
    // and the delete path through deleteEdges, so we don't forward here.
    setLocalEdges((prev) => applyEdgeChanges(changes, prev) as RFEdge[]);
  }, []);

  // MergeMap pattern (ported from React Flow's collaborative-pro example):
  // a per-doc cache holding {remote, local} pairs. `remote` is the Yjs
  // snapshot for a node; `local` is what React Flow renders, which extends
  // remote with fields RF manages locally (selected, measured, mid-drag
  // position, mid-resize style). On every yjsNodes change we call
  // `nodeMap.merge(yjsNodes)` which returns a new locals array — items with
  // unchanged remote refs reuse their cached local (stable identity → RF
  // skips re-render); items with changed remote get re-derived merging old
  // local + new remote.
  //
  // Why MergeMap and not the previous useState/useRef merge: the old
  // version racetracked the previous Yjs snapshot via a useRef and compared
  // yNode references inside a setLocalNodes closure. When two yjsNodes
  // updates landed before React committed the first setState, the ref was
  // overwritten before the first state-setter closure ran, mis-classifying
  // freshly-arrived remote nodes as "unchanged" and returning the
  // (undefined) local — silently dropping nodes from the rendered set
  // ("node count diverges between two collaborating windows; reload
  // restores missing nodes"). MergeMap holds the cache outside React, so
  // two consecutive merges are two consecutive function calls — no race.
  //
  // The map is recreated when `doc` changes so opening a different canvas
  // doesn't carry stale entries. `useNodesFromYjs` returns RFCanvasNode
  // (the engine-internal shape); we treat that as both Remote and Local,
  // letting RF's per-render mutations populate the local-only fields via
  // setNodeChanges below.
  const nodeMap = useMemo(
    () =>
      new MergeMap<RFNode, RFNode>({
        getId: (n) => n.id,
        deriveLocal: (n) => n,
      }),
    [],
  );
  const pendingSelectRef = useRef<string | null>(pendingSelectId ?? null);
  useEffect(() => {
    if (pendingSelectId != null) pendingSelectRef.current = pendingSelectId;
  }, [pendingSelectId]);

  const [localNodes, setLocalNodes] = useState<RFNode[]>([]);
  useEffect(() => {
    const merged = nodeMap.merge(yjsNodes as unknown as RFNode[]);
    const withPendingSelect = applyPendingSelect(merged, pendingSelectRef);
    const withClassName = nodeClassName
      ? withPendingSelect.map((n) => {
          const cls = nodeClassName(n);
          return cls ? { ...n, className: cls } : n;
        })
      : withPendingSelect;
    setLocalNodes(withClassName);
  }, [yjsNodes, nodeMap, nodeClassName]);

  // Derive zOrder from the node array order (Y.Map iteration order = insertion order).
  const zOrder = useMemo(() => yjsNodes.map((n) => n.id), [yjsNodes]);
  const { onKeyDown: onCanvasKeyDown } = useCanvasKeyboard({ zOrder });

  // Parent-label resolver: maps a parentId → the parent's display label
  // (frame.data.title for frames; null for everything else). Renderers consume
  // this via ParentLabelContext for the "(in <frame>)" aria-label suffix.
  const parentLabelResolver = useCallback(
    (parentId: string): string | null => {
      const parent = yjsNodes.find((n) => n.id === parentId);
      if (!parent || parent.type !== 'frame') return null;
      const title = (parent.data as Record<string, unknown>)?.title;
      return typeof title === 'string' && title.length > 0 ? title : null;
    },
    [yjsNodes],
  );

  const drop = useFrameDropTarget();
  const autoAttach = useAutoAttach(doc, clientId);
  const { deleteFrame } = useFrameDelete(doc, clientId);
  const { onNodesChange: syncNodeChanges, flushImmediate: flushSyncImmediate } = useYjsNodeSync(doc, clientId);
  // Nodes whose position was just committed by useAutoAttach — block the
  // stale RF position change that arrives after drag-stop from overwriting it.
  const attachedNodeIds = useRef(new Set<string>());
  const { applyHelperLines, rebuildIndex, horizontal: helperH, vertical: helperV } = useHelperLines();
  const reactFlow = useReactFlow();
  const { getInternalNode } = reactFlow;
  const screenToCanvas = useScreenToCanvasCoords();
  useHorizontalScrollPan();
  const { handlers: contextMenuHandlers, menuRef: contextMenuRef } = useCanvasContextMenu();

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    // Generic flow:
    //   1. Apply ALL changes locally via RF's own applyNodeChanges. This
    //      captures every internal flag RF needs (selected, measured,
    //      dragging, resizing, mid-drag position, mid-resize dimensions)
    //      onto the local node objects. Without this step RF detaches its
    //      gesture state-machines mid-drag/resize because the next render
    //      lacks those flags.
    //   2. Write the updated locals back into the MergeMap so the cache is
    //      authoritative for what RF sees next render.
    //   3. Decide which changes to forward to Yjs. Authored data (final
    //      position, settled dimensions, add/remove/replace) goes to Yjs.
    //      Mid-gesture changes are local-only because Yjs is settled-state
    //      storage. Mirrors how the OLD engine code worked, but with
    //      MergeMap as the cache layer instead of a useState array — so
    //      remote-arrived nodes still flow in correctly via merge().
    //   4. Push the MergeMap's current locals into React state so RF
    //      re-renders with the gesture flags.
    let effectiveChanges = changes;
    setLocalNodes(prev => {
      effectiveChanges = applyHelperLines(changes, prev);
      const updated = applyNodeChanges(effectiveChanges, prev) as RFNode[];
      // Mirror updated locals into the MergeMap. Each cache entry's `remote`
      // stays whatever Yjs last produced; `local` becomes the post-changes
      // version with all RF internal flags applied.
      for (const node of updated) {
        const cached = nodeMap.get(node.id);
        // remote stays the same; only local changes. If the entry doesn't
        // exist yet (RF emitted an add change before our Yjs observer fired),
        // seed it with the local as both — the next merge() will overwrite
        // remote with the authoritative Yjs value.
        nodeMap.set(node.id, cached?.remote ?? node, node);
      }
      return updated;
    });

    // Children of currently-collapsed frames must NOT have position changes
    // forwarded to Yjs. Read collapsed-frame ids and the parentId of each
    // candidate child directly from the Yjs Y.Map (not from localNodes):
    // when collapse first fires, the Yjs observe re-derives the snapshot
    // with hidden:true on children, but React hasn't committed that
    // localNodes update yet — so localNodes still says hidden:false and
    // any clamp-driven position change for a child would slip past a
    // localNodes-based filter. Reading Yjs directly here bypasses the
    // React-state lag.
    const yjsNodesMap = roots.nodes;
    const collapsedParentIds = new Set<string>();
    yjsNodesMap.forEach((n) => {
      if (!(n instanceof Y.Map)) return;
      if (n.get('type') !== 'frame') return;
      const data = n.get('data');
      if (data instanceof Y.Map && data.get('collapsed') === true) {
        const fid = n.get('id');
        if (typeof fid === 'string') collapsedParentIds.add(fid);
      }
    });
    const isChildOfCollapsedFrame = (nodeId: string): boolean => {
      const yNode = yjsNodesMap.get(nodeId);
      if (!(yNode instanceof Y.Map)) return false;
      const parentId = yNode.get('parentId');
      return typeof parentId === 'string' && collapsedParentIds.has(parentId);
    };

    // Forward authored changes to Yjs. `position` writes go through even
    // mid-drag (collaborative live drag); `dimensions` writes only when
    // resize is settled (Yjs is for committed sizes — mid-resize sizes
    // would round-trip back through observe and conflict with RF's local
    // resize state machine). select is purely local. Position changes for
    // children of collapsed frames are dropped — see comment above.
    const yjsChanges = effectiveChanges.filter(c => {
      if (c.type === 'select') return false;
      if (c.type === 'dimensions') return !(c as { resizing?: boolean }).resizing;
      if (c.type === 'position' && isChildOfCollapsedFrame((c as { id: string }).id)) return false;
      // Block stale absolute-position writes for nodes just attached to a frame
      // by useAutoAttach. RF emits a final position change after drag-stop that
      // still carries the pre-attach absolute position; letting it through
      // overwrites the correct frame-local position autoAttach committed.
      if (c.type === 'position' && attachedNodeIds.current.has((c as { id: string }).id)) {
        attachedNodeIds.current.delete((c as { id: string }).id);
        return false;
      }
      return true; // position, add, remove, replace
    });
    if (yjsChanges.length > 0) {
      syncNodeChanges(yjsChanges);
    }
  }, [roots, syncNodeChanges, nodeMap, applyHelperLines, attachedNodeIds]);

  const dragCache = useFrameDragCache();

  // Project React Flow nodes (parent-relative positions on nested children)
  // into the absolute-position SpatialNode shape geometry helpers expect.
  // The dragCache fallback handles the frame-deleted-mid-drag race per
  // client.md §6. See spatialProjection.ts for the full algorithm.
  const spatialNodes = useMemo(
    () => projectSpatialNodes(localNodes, dragCache.getCachedAbsolute),
    [localNodes, dragCache.getCachedAbsolute],
  );

  const onNodeDragStart = useCallback(
    (_e: React.MouseEvent | React.TouchEvent, node: RFNode) => {
      // Snapshot the dragged node's parent-chain absolute positions so a
      // peer-deletion of any ancestor mid-drag falls back to the cache
      // rather than warping the child to canvas origin (client.md §6).
      const parentIds: string[] = [];
      let pid = node.parentId;
      while (pid) {
        parentIds.push(pid);
        const internal = getInternalNode(pid);
        if (!internal) break;
        const parentRF = localNodes.find(n => n.id === pid);
        pid = parentRF?.parentId;
      }
      dragCache.recordDragStart(parentIds, (id) => {
        const internal = getInternalNode(id);
        const abs = internal?.internals.positionAbsolute;
        return abs ? { x: abs.x, y: abs.y } : null;
      });
    },
    [dragCache, getInternalNode, localNodes],
  );

  const onNodeDrag = useCallback((_e: React.MouseEvent | React.TouchEvent, node: RFNode) => {
    // v12: absolute position lives on InternalNode.internals.positionAbsolute;
    // node.position is parent-relative when parentId is set. Per client.md §6,
    // drop-target detection probes the dragged node's bbox center.
    const internal = getInternalNode(node.id);
    const abs = internal?.internals.positionAbsolute ?? node.position;
    const measuredWidth = internal?.measured?.width ?? 0;
    const measuredHeight = internal?.measured?.height ?? 0;
    const center = {
      x: abs.x + measuredWidth / 2,
      y: abs.y + measuredHeight / 2,
    };
    drop.onDrag(center, spatialNodes, [node.id]);
  }, [drop, getInternalNode, spatialNodes]);

  const onNodeDragStop = useCallback((_e: React.MouseEvent | React.TouchEvent, node: RFNode) => {
    // v12: read absolute via getInternalNode (Node.position is parent-relative
    // when the dragged node has a parentId). useAutoAttach.commit subtracts
    // the target frame's origin to compute the new frame-local position.
    const internal = getInternalNode(node.id);
    const abs = internal?.internals.positionAbsolute ?? node.position;
    const targetFrameId = drop.targetFrameId;

    if (targetFrameId) {
      // Flush any buffered position changes BEFORE committing the frame-local
      // position, so the throttler can't overwrite autoAttach's commit later.
      flushSyncImmediate();
      // Mark this node so the post-drag-stop onNodesChange position change
      // (RF's final dragging:false update, still carrying the absolute position)
      // is blocked from writing back to Yjs and overwriting autoAttach's result.
      attachedNodeIds.current.add(node.id);
    }

    autoAttach.commit({
      nodeId: node.id,
      absoluteEndPos: { x: abs.x, y: abs.y },
      targetFrameId,
    });
    drop.reset();
    dragCache.clearCache();
    rebuildIndex(reactFlow.getNodes());
  }, [autoAttach, drop, getInternalNode, dragCache, rebuildIndex, reactFlow, flushSyncImmediate]);

  // deleteNodeIds is called both by ReactFlow's onNodesDelete (RFNode[]) and
  // CanvasKeyboardShortcuts (string[]). Frames need special handling (child
  // promotion); all others are plain removes.
  const deleteNodeIds = useCallback((ids: string[]) => {
    const nodes = roots.nodes;
    doc.transact(() => {
      for (const id of ids) {
        const yNode = nodes.get(id);
        if (!yNode) continue;
        if (yNode.get('type') === 'frame') {
          deleteFrame(id);
        } else {
          nodes.delete(id);
        }
      }
    }, clientId);
  }, [roots, doc, clientId, deleteFrame]);

  const onNodesDelete = useCallback((deleted: RFNode[]) => {
    deleteNodeIds(deleted.map((n) => n.id));
  }, [deleteNodeIds]);

  const handlePaneClick = useCallback<NonNullable<ReactFlowProps['onPaneClick']>>(
    (e) => {
      if (!onPaneClick) return;
      const pos = screenToCanvas(e.clientX, e.clientY);
      const frameId = findFrameUnderPoint(pos, spatialNodes, []);
      onPaneClick(pos, frameId);
    },
    [onPaneClick, screenToCanvas, spatialNodes],
  );

  // During placement (onPaneClick present), RF routes a click landing on any
  // node to onNodeClick, not onPaneClick — so without this we'd never place a
  // node on top of an existing one. Frames place into themselves (skip the
  // title bar); any other node resolves the frame under the point instead.
  const handleNodeClick = useCallback<NonNullable<ReactFlowProps['onNodeClick']>>(
    (e, node) => {
      if (!onPaneClick) return;
      const pos = screenToCanvas(e.clientX, e.clientY);
      if (node.type === 'frame') {
        const target = e.target as HTMLElement;
        if (target.closest('[data-testid="frame-title-bar"]')) return;
        onPaneClick(pos, node.id);
        return;
      }
      const frameId = findFrameUnderPoint(pos, spatialNodes, []);
      onPaneClick(pos, frameId);
    },
    [onPaneClick, screenToCanvas, spatialNodes],
  );

  const dropContextValue = useMemo(
    () => ({ targetFrameId: drop.targetFrameId }),
    [drop.targetFrameId],
  );

  // aria-live announcement message for the active drop target. Empty when no
  // target is active so the polite region stays silent. Derived from the
  // parent-label resolver so it reuses the frame-title lookup the renderers
  // already use.
  const dropAnnouncement = useMemo(() => {
    if (!drop.targetFrameId) return '';
    const title = parentLabelResolver(drop.targetFrameId);
    return title ? `dropping into ${title}` : '';
  }, [drop.targetFrameId, parentLabelResolver]);

  // ─── Awareness layer wiring ─────────────────────────────────────────────
  // Mirror the React Flow collaborative-pro example pattern:
  //   1. Native `pointermove` listener attached to RF's INTERNAL canvas DOM
  //      node — guarantees the event fires regardless of how RF handles its
  //      own pointer events. A JSX `onMouseMove` on a wrapper div can be
  //      intercepted by RF's panel handlers in some interaction modes.
  //   2. `screenToFlowPosition()` for input + `<ViewportPortal>` (mounted by
  //      the awareness layer's render components) for output. Both let RF
  //      own the screen↔flow transform.
  //   3. Selection-bounds resolver returns FLOW-SPACE bounds; the outline
  //      overlay renders inside ViewportPortal so flow-space is correct.
  const domNode = useRFStore((s: ReactFlowState) => s.domNode);

  useEffect(() => {
    if (!domNode || (!reportPointer && !onCursorMove)) return;
    const onPointerMove = (e: PointerEvent) => {
      const flow = screenToCanvas(e.clientX, e.clientY);
      reportPointer?.({ x: flow.x, y: flow.y });
      onCursorMove?.({ x: flow.x, y: flow.y });
    };
    const onPointerLeave = () => {
      reportPointer?.(null);
    };
    domNode.addEventListener('pointermove', onPointerMove);
    domNode.addEventListener('pointerleave', onPointerLeave);
    return () => {
      domNode.removeEventListener('pointermove', onPointerMove);
      domNode.removeEventListener('pointerleave', onPointerLeave);
    };
  }, [domNode, reportPointer, onCursorMove, screenToCanvas]);

  // Flow-space bounds for the selection-outline overlay. ViewportPortal
  // applies the viewport transform; we just return the node's pre-transform
  // position + measured size.
  const nodeBoundsResolver = useCallback(
    (nodeId: string) => {
      const internal = getInternalNode(nodeId);
      if (!internal) return null;
      const abs = internal.internals.positionAbsolute;
      const w = internal.measured?.width ?? 0;
      const h = internal.measured?.height ?? 0;
      return { x: abs.x, y: abs.y, width: w, height: h };
    },
    [getInternalNode],
  );

  const onSelectionChange = useCallback(
    (params: OnSelectionChangeParams) => {
      reportSelection?.(params.nodes.map((n) => n.id));
      reportEdgeSelection?.(params.edges.map((e) => e.id));
    },
    [reportSelection, reportEdgeSelection],
  );

  // Connector creation — fires when the user finishes a drag-from-handle
  // gesture on a target node. We delegate the Yjs write to a pure helper so
  // the engine only owns the React Flow→Yjs glue here. createdBy is left
  // empty for the engine; the shell can override post-create the same way
  // it does for nodes (see createNode.ts:53).
  const onConnect = useCallback(
    (conn: Connection) => {
      createEdgeFromConnection(doc, clientId, '', conn, resolvedBinding);
    },
    [doc, clientId, resolvedBinding],
  );

  const onDeleteEdges = useCallback(
    (ids: string[]) => deleteEdges(doc, clientId, ids, resolvedBinding),
    [doc, clientId, resolvedBinding],
  );

  // EdgeActions — feeds the right-click context menu (and any other consumer
  // that needs single-edge actions). Mirrors NodeActions in shape but keeps
  // the surface tight: the v1 edge menu only does z-order / lock / delete.
  // Lock currently delegates to a single `onToggleLock` (matches nodes); the
  // menu reads the edge's current `locked` flag and labels itself.
  const edgeActions = useMemo<EdgeActions>(() => ({
    onDelete: (id) => deleteEdges(doc, clientId, [id], resolvedBinding),
    onToggleLock: (id) => {
      const e = roots.edges.get(id);
      if (!e) return;
      setEdgeLocked(doc, clientId, id, !(e.get('locked') === true), resolvedBinding);
    },
    onBringToFront: (id) => bringToFront(doc, clientId, id, resolvedBinding),
    onSendToBack:   (id) => sendToBack(doc, clientId, id, resolvedBinding),
    onBringForward: (id) => bringForward(doc, clientId, id, resolvedBinding),
    onSendBackward: (id) => sendBackward(doc, clientId, id, resolvedBinding),
  }), [roots, doc, clientId, resolvedBinding]);

  // Drag the source/target endpoint circle of a selected edge to a new
  // node — RF emits onReconnect with the old edge and the new connection.
  // We re-anchor by writing the new source/target to the existing Y.Map
  // (no new edge id) so the edge keeps its identity (label, kind,
  // styling, undo history all preserved).
  const onReconnect = useCallback(
    (oldEdge: { id: string }, newConn: Connection) => {
      reconnectEdgeYjs(doc, clientId, {
        edgeId: oldEdge.id,
        source: newConn.source,
        target: newConn.target,
        sourceHandle: newConn.sourceHandle,
        targetHandle: newConn.targetHandle,
      }, resolvedBinding);
    },
    [doc, clientId, resolvedBinding],
  );

  // Tracks the edge id currently being reconnected. NulaConnectionLine
  // reads this ref through InFlightReconnectContext so the rubber-band
  // line uses that edge's customised stroke/width/style/curvature
  // instead of the new-edge default. A ref (not state) avoids
  // re-rendering every consumer on each pointer move during the drag.
  const inFlightReconnectRef = useRef<string | null>(null) as InFlightReconnectRef;
  const onReconnectStart = useCallback(
    (_e: unknown, edge: { id: string }) => { inFlightReconnectRef.current = edge.id; },
    [],
  );
  const onReconnectEnd = useCallback(
    () => { inFlightReconnectRef.current = null; },
    [],
  );

  return (
    <CanvasBindingContext.Provider value={binding ?? TOP_LEVEL_BINDING}>
    <CanvasReadOnlyContext.Provider value={readOnly}>
    <CanvasDocContext.Provider value={doc}>
    <ConnectorModeContext.Provider value={!!connectorMode}>
    <EdgeActionsContext.Provider value={readOnly ? null : edgeActions}>
    <InFlightReconnectContext.Provider value={inFlightReconnectRef}>
    <FrameDropTargetContext.Provider value={dropContextValue}>
      <ParentLabelProvider resolver={parentLabelResolver}>
        <div
          id="canvas-content"
          className={styles.canvasRoot}
          tabIndex={0}
          data-testid="canvas-engine"
          data-readonly={readOnly ? 'true' : undefined}
          onKeyDown={readOnly ? undefined : onCanvasKeyDown}
        >
          <div className={styles.engine}>
            <ReactFlow
              nodes={localNodes}
              edges={localEdges}
              onEdgesChange={readOnly ? undefined : onEdgesChange}
              nodeTypes={nodeTypeMap}
              edgeTypes={edgeTypeMap}
              connectionMode={ConnectionMode.Loose}
              connectionLineComponent={NulaConnectionLine}
              onConnect={readOnly ? undefined : onConnect}
              onReconnect={readOnly ? undefined : onReconnect}
              onReconnectStart={readOnly ? undefined : onReconnectStart}
              onReconnectEnd={readOnly ? undefined : onReconnectEnd}
              defaultViewport={defaultViewport ?? { x: 0, y: 0, zoom: 1 }}
              minZoom={0.1}
              maxZoom={4}
              proOptions={{ hideAttribution: true }}
              onNodesChange={readOnly ? undefined : onNodesChange}
              onNodesDelete={readOnly ? undefined : onNodesDelete}
              onNodeDragStart={readOnly ? undefined : onNodeDragStart}
              onNodeDrag={readOnly ? undefined : onNodeDrag}
              onNodeDragStop={readOnly ? undefined : onNodeDragStop}
              onPaneClick={(!readOnly && onPaneClick) ? handlePaneClick : undefined}
              onNodeClick={(!readOnly && onPaneClick) ? handleNodeClick : undefined}
              onSelectionChange={onSelectionChange}
              onViewportChange={onViewportChange}
              nodesDraggable={readOnly ? false : undefined}
              nodesConnectable={readOnly ? false : undefined}
              elementsSelectable={(readOnly || onPaneClick) ? false : undefined}
              selectNodesOnDrag={readOnly ? false : true}
              deleteKeyCode={null}
              multiSelectionKeyCode="Shift"
              onNodeContextMenu={readOnly ? undefined : contextMenuHandlers.onNodeContextMenu}
              onEdgeContextMenu={readOnly ? undefined : contextMenuHandlers.onEdgeContextMenu}
              onPaneContextMenu={readOnly ? undefined : contextMenuHandlers.onPaneContextMenu}
              {...(!readOnly ? pointerMode : undefined)}
            >
              <Background variant={BackgroundVariant.Dots} gap={16} size={1} />

              {/* Per-color dot marker defs. One <marker> per unique stroke
                  color used by dot-endpoint edges. Avoids fill="context-stroke"
                  which is unsupported in WebKit (macOS Tauri). */}
              <svg className={styles.markerDefs} aria-hidden>
                <defs>
                  {collectDotMarkerColors(roots).map((color) => (
                    <marker
                      key={color}
                      id={`nula-dot-${safeColorId(color)}`}
                      viewBox="0 0 10 10"
                      refX="5"
                      refY="5"
                      markerWidth="6"
                      markerHeight="6"
                      markerUnits="strokeWidth"
                    >
                      <circle cx="5" cy="5" r="4" fill={color} />
                    </marker>
                  ))}
                </defs>
              </svg>

              <Panel position="bottom-left">
                {!hideControls && (
                  <CanvasZoomControls
                    onOpenFramesPanel={onOpenFramesPanel}
                    framesPanelOpen={framesPanelOpen}
                  />
                )}
                {/* Imperative bridge — mounts inside the RF provider tree
                    so portal-sibling consumers (FramesPanel, future
                    panels) can drive viewport / selection from outside.
                    No-op when there's no <ImperativeProvider> ancestor. */}
                <Imperative />
              </Panel>

              {/* Local-side broadcaster: subscribes to useConnection() and
                  forwards the live drag state to awareness. Must mount inside
                  <ReactFlow> so the hook resolves. Render-only when readOnly
                  is false — read-only canvases never originate drags. */}
              {!readOnly && reportInFlightEdge ? (
                <InFlightEdgeBroadcaster reportInFlightEdge={reportInFlightEdge} />
              ) : null}

              {/* One-shot fit-to-content on mount when requested (diff/snapshot panes). */}
              <FitViewOnLoad enabled={!!fitOnMount} />

              {/* Shell-injected, RF-context-dependent helpers (e.g. preview snapshotter). */}
              {inFlowSlot}

              {/* Remote awareness overlay — must mount INSIDE <ReactFlow> so
                  ViewportPortal can access the RF store + viewport-transformed
                  parent. */}
              {remoteStates && remoteStates.size > 0 ? (
                <CanvasAwarenessLayer
                  remoteStates={remoteStates}
                  nodeBoundsResolver={nodeBoundsResolver}
                />
              ) : null}
              {isDrawing && <DrawingOverlay doc={doc} strokeColor={drawingColor} strokeWidth={drawingWidth} strokeOpacity={drawingOpacity} />}
              {isErasing && <EraserOverlay doc={doc} />}
              <HelperLinesRenderer horizontal={helperH} vertical={helperV} />
              <MultiSelectionToolbar />
              {!readOnly && <CanvasContextMenu menuRef={contextMenuRef} />}
              {!readOnly && <CanvasKeyboardShortcuts undoApi={undoApi} onNodesDelete={deleteNodeIds} onEdgesDelete={onDeleteEdges} />}
            </ReactFlow>
          </div>
          {localNodes.length === 0 && localEdges.length === 0 ? <EmptyCanvas /> : null}
          {/* M4 carryover — aria-live region narrating the active frame
              drop target. Visually hidden via CSS .dropAnnouncer; updates
              fire as drop.targetFrameId changes. */}
          <div
            data-testid="drop-target-announcer"
            className={styles.dropAnnouncer}
            aria-live="polite"
            aria-atomic="true"
          >
            {dropAnnouncement}
          </div>
        </div>
      </ParentLabelProvider>
    </FrameDropTargetContext.Provider>
    </InFlightReconnectContext.Provider>
    </EdgeActionsContext.Provider>
    </ConnectorModeContext.Provider>
    </CanvasDocContext.Provider>
    </CanvasReadOnlyContext.Provider>
    </CanvasBindingContext.Provider>
  );
}
